import DynamicForm, { Field } from "components/dynamic/DynamicForm";
import DynamicFilter from "components/filter/DynamicFilter";
import MobileFilterDrawer from "components/filter/MobileFilterDrawer";
import ConfirmModal from "components/modal/ConfirmModal";
import HeaderBox from "components/navbar/HeaderBox";
import DynamicTable from "components/table/DynamicTable";
import Pagination from "components/table/Pagination";
import { useModal } from "hooks/useModal";
import i18n from "i18n";
import moment from "moment-jalaali";
import { useState } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  useDeleteRequestMutation,
  useGetRequestsQuery,
  useUpdateRequestMutation,
} from "reduxFiles/api/userApi";
import { enumToOptions } from "services/helper";
import { RequestType, RequestStatusType } from "types/userTypes";
import { Column, TableAction } from "types/globalTypes";
import { Modal } from "components/modal";
import Button from "components/button/Button";

const statusStyles: Record<RequestStatusType, string> = {
  [RequestStatusType.PENDING]:
    "bg-amber-50 text-amber-700 ring-1 ring-amber-200",
  [RequestStatusType.IN_PROGRESS]:
    "bg-blue-50 text-blue-700 ring-1 ring-blue-200",
  [RequestStatusType.RESOLVED]:
    "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
};

const StatusBadge = ({
  status,
  label,
}: {
  status: RequestStatusType;
  label: string;
}) => (
  <span
    className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${
      statusStyles[status] ?? "bg-gray-100 text-gray-700 ring-1 ring-gray-200"
    }`}
  >
    {label}
  </span>
);

const InfoRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex flex-col gap-0.5 border-b border-gray-100 py-3 last:border-b-0 dark:border-gray-800 sm:flex-row sm:items-start sm:gap-4">
    <span className="min-w-[130px] text-xs font-medium text-gray-800 dark:text-gray-400">
      {label}
    </span>
    <span className="flex-1 break-words text-sm text-gray-800 dark:text-gray-200">
      {value || <span className="text-gray-400">—</span>}
    </span>
  </div>
);

const Requests = () => {
  // hooks
  const { t } = useTranslation();
  const lang = i18n.language;

  // states
  const [item, setItem] = useState<RequestType | null>(null);
  const confirmModal = useModal();
  const modal = useModal();
  const detailModal = useModal();

  // filters
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });

  // apis
  const { data, isFetching, refetch } = useGetRequestsQuery({
    page: pagination.page,
    limit: pagination.limit,
    filters: filters,
  });

  const [updateApi, { isLoading: isUpdating }] = useUpdateRequestMutation();
  const [deleteApi, { isLoading: isDeleting }] = useDeleteRequestMutation();

  // form and table
  const fields: Field[] = [
    {
      name: "search",
      label: t("requests.search"),
      type: "text",
      filtered: true,
    },
    {
      name: "status",
      label: t("requests.status"),
      type: "select",
      options: enumToOptions(RequestStatusType, t, "requests.statuses"),
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "descriptionAdmin",
      label: t("requests.descriptionAdmin"),
      type: "textarea",
      formed: true,
    },
    {
      name: "startDate",
      label: t("table.startDate"),
      type: "date",
      filtered: true,
    },
    {
      name: "endDate",
      label: t("table.endDate"),
      type: "date",
      filtered: true,
    },
  ];

  const columns: Column<RequestType>[] = [
    {
      key: "createdAt",
      title: t("table.createdAt"),
      render: (row) =>
        moment(row.createdAt).format(
          lang === "fa" ? "jYYYY/jMM/jDD HH:mm" : "YYYY/MM/DD HH:mm"
        ),
    },
    {
      key: "fullname",
      title: t("requests.fullname"),
    },
    { key: "companyname", title: t("requests.companyName") },
    { key: "mobile", title: t("requests.mobile") },
    {
      key: "status",
      title: t("requests.status"),
      render: (row) => t(`requests.statuses.${row.status}`),
    },
  ];

  const actions: TableAction<RequestType>[] = [
    {
      label: t("buttons.view"),
      onClick: (row) => {
        setItem(row);
        detailModal.openModal();
      },
      color: "primary",
      see: true,
    },
    {
      label: t("requests.editReq"),
      onClick: (row) => {
        setItem(row);
        modal.openModal();
      },
      color: "secondary",
      edit: true,
    },
    {
      label: t("requests.deleteReq"),
      delete: true,
      onClick: (row) => {
        setItem(row);
        confirmModal.openModal();
      },
      color: "error",
    },
  ];

  // functions
  const handleFilterSubmit = (filterData: FieldValues) => {
    setFilters(filterData);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination({
      page: 1,
      limit: newLimit,
    });
  };

  const handleSubmit = async (formData: FieldValues) => {
    try {
      await updateApi({ id: item.id, data: formData }).unwrap();
      modal.closeModal();
      setTimeout(() => {
        setItem(null);
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async () => {
    try {
      if (!item) return;
      await deleteApi(item.id).unwrap();
      confirmModal.closeModal();
      setTimeout(() => {
        setItem(null);
      }, 100);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className="mt-5 h-full">
        <HeaderBox
          title={t("requests.requestList")}
          filterBtn={{
            title: t("buttons.filters"),
            handler: () => setShowFilters((prev) => !prev),
          }}
          refreshBtn={refetch}
        />

        <MobileFilterDrawer
          open={showFilters}
          onClose={() => setShowFilters((prev) => !prev)}
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          loading={isFetching}
        />
        <DynamicFilter
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          defaultValues={{}}
          loading={isFetching}
        />

        <DynamicTable<RequestType>
          columns={columns}
          data={data?.items ?? []}
          actions={actions}
          loading={isFetching}
          pagination={pagination}
        />
        <Pagination
          currentPage={pagination?.page || 1}
          totalPages={data?.pageCount || 1}
          onPageChange={(newPage) => {
            setPagination((prev) => ({ ...prev, page: newPage }));
          }}
          totalItems={data?.total || 0}
          currentLimit={pagination.limit}
          onLimitChange={handleLimitChange}
        />

        <DynamicForm
          open={modal.isOpen}
          onClose={() => {
            setItem(null);
            modal.closeModal();
          }}
          onSubmit={handleSubmit}
          fields={fields?.filter((f) => f.formed)}
          defaultValues={item || {}}
          title={t("requests.editReq")}
          isEdit={!!item}
          loading={isUpdating}
        />

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => {
            setItem(null);
            confirmModal.closeModal();
          }}
          handler={handleDelete}
          loading={isDeleting}
          title={t("requests.deleteReq")}
          desc={t("actions.confirmDeleteDesc", { name: item?.fullname || "" })}
        />

        {/* Detail Modal */}
        <Modal
          isOpen={detailModal.isOpen}
          onClose={() => {
            setItem(null);
            detailModal.closeModal();
          }}
          showCloseButton={true}
          maxWidth="max-w-lg"
          className="p-6 lg:p-8"
        >
          {item && (
            <div className="space-y-5">
              {/* Header */}
              <div className="flex flex-col items-center gap-2 text-center">
                <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90 md:text-xl">
                  {t("requests.requestItem")}
                </h4>
                <StatusBadge
                  status={item.status}
                  label={t(`requests.statuses.${item.status}`)}
                />
              </div>

              {/* Body */}
              <div className="rounded-xl border border-gray-100 bg-gray-50/50 px-4 dark:border-gray-800 dark:bg-gray-900/40">
                <InfoRow
                  label={t("table.createdAt")}
                  value={moment(item.createdAt).format(
                    lang === "fa" ? "jYYYY/jMM/jDD HH:mm" : "YYYY/MM/DD HH:mm"
                  )}
                />
                <InfoRow label={t("requests.fullname")} value={item.fullname} />
                <InfoRow
                  label={t("requests.companyName")}
                  value={item.companyname}
                />
                <InfoRow label={t("requests.email")} value={item.email} />
                <InfoRow
                  label={t("requests.mobile")}
                  value={
                    item.mobile ? <span dir="ltr">{item.mobile}</span> : null
                  }
                />
                <InfoRow
                  label={t("requests.description")}
                  value={item.description}
                />
                <InfoRow
                  label={t("requests.descriptionAdmin")}
                  value={item.descriptionAdmin}
                />
              </div>

              {/* Footer */}
              <div className="flex w-full items-center justify-center">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setItem(null);
                    detailModal.closeModal();
                  }}
                  className="min-w-[120px]"
                >
                  {t("buttons.close")}
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
};

export default Requests;
